import {
  useCallback, useContext, useEffect, useMemo
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { TranslationContext } from "Components/Generic/Translator";
import { isIndexInRange } from "Utils/Array";
import { getName, updateAllItems, updateProjectItem } from "Utils/Item";
import { extract } from "Utils/Object";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace } from "Utils/String";
import { Translator } from "Utils/Translator";
import { IAddressValidationResult } from "Model/Omikron/IAddressValidationResult";
import { IItem } from "Model/Storage/IItem";
import { addressValidationCacheSet, projectsUpdateStart } from "Store/Actions";
import { selectAddressValidationCache, selectAddressValidationResults } from "Store/Selectors/Omikron";
import { IProject } from "Model/Storage/IProject";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { ProjectItemContext } from "../ProjectItemContext/ProjectItemContext";
import { IUseAddressValidation } from "./IUseAddressValidation";
import { useProject } from "../Hooks/UseProject";
import { AV_RESULT_COLUMN_LIST } from "./AddressValidationColumns";

export function useAddressValidation(): IUseAddressValidation {
  const translator = useContext(TranslationContext);
  const { preflightAuthentication } = useAuthenticationPreflight();
  const { projectId, projectItemId } = useContext(ProjectItemContext);
  const dispatch = useDispatch();
  const project = useProject(projectId);
  const avResults = useSelector(selectAddressValidationResults);
  const cache = useSelector(selectAddressValidationCache);

  const results: IAddressValidationResult[][] = useMemo(() => {
    if (project == null) {
      return [];
    }

    return avResults.map((resultList) => resultList.map<IAddressValidationResult>((result) => ({
      ...result,
      "@resultTitle": getText(translator, result),
      "@isResultSelected": hasPreselectableStatus(result)
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avResults, dispatch, project, translator]);

  const preselectedProject = useMemo<IProject>(() => {
    if (project == null) {
      return null;
    }

    let updatedProject = null;
    for (const result of results.flat()) {
      if (result[`@isResultSelected`]) {
        updatedProject = applyResult(updatedProject ?? cache ?? project, result);
      }
    }

    return updatedProject;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, project]);

  useEffect(() => {
    if (project == null) {
      return;
    }

    const projectToProcess = cache ?? preselectedProject ?? project;

    dispatch(
      addressValidationCacheSet(
        updateProjectItem(projectToProcess, projectItemId, (item) => ({ ...item, "@isAV": true }))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectItemId, preselectedProject]);

  const onSelect = useCallback(
    (result: IAddressValidationResult) => {
      if (result == null) {
        return;
      }

      dispatch(addressValidationCacheSet(applyResult(cache ?? project, result)));
    },
    [cache, dispatch, project]
  );

  const onIsRelevant = useCallback(
    (id: string) => {
      dispatch(
        addressValidationCacheSet(
          updateProjectItem(cache ?? project, id, (item: IItem) => ({ ...item, "@isRelevant": !item[`@isRelevant`] }))
        )
      );
    },
    [cache, dispatch, project]
  );

  const onSave = useCallback(async () => {
    if (cache == null) {
      return;
    }

    await preflightAuthentication();
    dispatch(projectsUpdateStart([updateAllItems(cache, (item) => ({ ...item, "@isAV": true }))]));
    dispatch(addressValidationCacheSet(null));
  }, [cache, dispatch, preflightAuthentication]);

  return {
    cache,
    onIsRelevant,
    onSave,
    onSelect,
    results
  };
}

function getText(translator: Translator, result: IAddressValidationResult): string {
  if (result == null) {
    return null;
  }

  if (result[`@isOrigin`]) {
    return getName(result);
  }

  const status = result.pkStatus;
  const statusDetail = result.pkStatusDetail;

  if (isNullOrWhiteSpace(status)) {
    return statusDetail;
  }

  const statusDetailList = isNotNullOrWhiteSpace(statusDetail) ? statusDetail.split(`, `) : [];

  return status
    .split(` `)
    .filter((code) => isNotNullOrWhiteSpace(code))
    .map((code, index) => translator.get(`av.status.${code}`, isIndexInRange(statusDetailList, index) ? statusDetailList[index] : ``))
    .join(`, `);
}

function hasPreselectableStatus(result: IAddressValidationResult): boolean {
  if (result == null) {
    return false;
  }

  const preSeleactableStates = [`ok`, `wk`];
  return preSeleactableStates.includes(result.pkStatus?.trim()?.toLowerCase());
}

function applyResult(project: IProject, result: IAddressValidationResult): IProject {
  return updateProjectItem(project, result[`@id`], (item) => ({
    ...item,
    ...extract(result, ...AV_RESULT_COLUMN_LIST),
    "@isAV": true
  }));
}
